import { PageSlug } from '@/domain/value-object/PageSlug'
import { PlanLimitService } from '@/domain/service/PlanLimitService'
import { PageCreated } from '@/domain/events/PageEvents'
import type { ICreatePagePort, CreatePageCommand } from '@/application/port/in/ICreatePagePort'
import type { ITenantRepository } from '@/application/port/out/ITenantRepository'
import type { IPageRepository } from '@/application/port/out/IPageRepository'
import type { IDomainEventBus } from '@/application/port/out/IDomainEventBus'
import type { Page } from '@/domain/model/Page'

const planLimits = new PlanLimitService()

export class CreatePageService implements ICreatePagePort {
  constructor(
    private readonly tenantRepository: ITenantRepository,
    private readonly pageRepository: IPageRepository,
    private readonly eventBus: IDomainEventBus,
  ) {}

  async execute(command: CreatePageCommand): Promise<Page> {
    const tenant = await this.tenantRepository.findById(command.tenantId)
    if (!tenant) throw new Error('Tenant not found')

    if (!command.title.trim()) throw new Error('Page title is required')

    const slug = PageSlug.from(command.slug)

    const existing = await this.pageRepository.findBySiteId(command.siteId)
    if (existing.some((p) => p.slug === slug.toString())) {
      throw new Error(`A page with slug "/${slug}" already exists in this site`)
    }

    planLimits.assertCanCreatePage(tenant, existing.length)

    const page = await this.pageRepository.create({
      siteId:  command.siteId,
      title:   command.title.trim(),
      slug:    slug.toString(),
      isHome:  command.isHome ?? false,
    })

    await this.eventBus.dispatch([
      new PageCreated(page.id, page.siteId, page.slug, page.isHome),
    ])

    return page
  }
}
